/**
 * Credits Display Component
 * 
 * Shows user's credit balance with styling based on amount.
 * Hover to see recent credit history with realtime updates.
 */

import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useCredits } from '../contexts/CreditsContext';
import { useAuth } from '../contexts/AuthContext';
import { CreditsIcon } from './ui/CreditsIcon';
import './CreditsDisplay.css';

interface CreditsDisplayProps {
  showLabel?: boolean;
  compact?: boolean;
  onClick?: () => void;
  tooltipMode?: 'history' | 'buy-only';
  onBuyCredits?: () => void;
}

export const CreditsDisplay: React.FC<CreditsDisplayProps> = ({ 
  showLabel = true, 
  compact = false,
  onClick,
  tooltipMode = 'history',
  onBuyCredits,
}) => {
  const { user } = useAuth();
  const { balance, loading, error, recentTransactions } = useCredits();
  const isStagingHost = window.location.hostname.includes('staging');
  const showRecentActivityTooltip = !import.meta.env.PROD || isStagingHost;
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const displayRef = useRef<HTMLDivElement>(null);
  
  // Keep track of last valid balance to prevent blinking during updates
  const lastValidBalanceRef = useRef<number>(balance);
  if (balance > 0 || !loading) {
    lastValidBalanceRef.current = balance;
  }
  const displayBalance = loading && lastValidBalanceRef.current > 0 ? lastValidBalanceRef.current : balance;

  // Don't show if not logged in
  if (!user) {
    return null;
  }

  const getBalanceClass = () => {
    if (displayBalance <= 0) return 'credits-empty';
    if (displayBalance < 10) return 'credits-low';
    if (displayBalance < 50) return 'credits-medium';
    return 'credits-good';
  };

  const formatBalance = (amount: number) => {
    return Math.floor(amount).toLocaleString('en-US');
  };

  const handleMouseEnter = () => {
    // Don't show tooltip on dashboard (buy-only mode has no hover)
    if (tooltipMode === 'buy-only' || !showRecentActivityTooltip) return;
    
    if (displayRef.current) {
      const rect = displayRef.current.getBoundingClientRect();
      const tooltipWidth = 300;
      const tooltipHeight = 420; // taller for 20 items
      const padding = 8;
      
      let x = rect.left;
      let y = rect.bottom + padding;
      
      // Check if tooltip would go off the right edge
      if (x + tooltipWidth > window.innerWidth - padding) {
        x = window.innerWidth - tooltipWidth - padding;
      }
      
      // Check if tooltip would go off the left edge
      if (x < padding) {
        x = padding;
      }
      
      // Check if tooltip would go off the bottom edge
      if (y + tooltipHeight > window.innerHeight - padding) {
        // Position above the element instead
        y = rect.top - tooltipHeight - padding;
        
        // If still off screen (top), position at top with padding
        if (y < padding) {
          y = padding;
        }
      }
      
      setTooltipPosition({ x, y });
    }
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getTransactionColor = (type: string, _amount: number) => {
    if (type === 'refund' || type === 'purchase' || type === 'bonus') {
      return 'var(--color-accent-success)';
    }
    return 'var(--color-accent-error)';
  };

  return (
    <>
      <div 
        ref={displayRef}
        className={`credits-display ${compact ? 'compact' : ''} ${getBalanceClass()}`}
        data-tutorial="credits-display"
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
      >
        <span className="credits-icon"><CreditsIcon size={14} /></span>
        {loading ? (
          <span className="credits-value loading">
            <span className="credits-value loading-dot" />
            <span className="credits-value loading-dot" />
            <span className="credits-value loading-dot" />
          </span>
        ) : error ? (
          <span className="credits-value error" title={error}>!</span>
        ) : (
          <span className="credits-value">{formatBalance(displayBalance)}</span>
        )}
        {showLabel && !compact && (
          <span className="credits-label">credits</span>
        )}
      </div>

      {/* Tooltip */}
      {showTooltip && ReactDOM.createPortal(
        tooltipMode === 'buy-only' ? (
          <div 
            className="credits-history-tooltip"
            style={{
              position: 'fixed',
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              zIndex: 10000
            }}
          >
            <div className="tooltip-header">
              <span className="tooltip-title">Credits</span>
            </div>
            <div className="tooltip-content" style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
              <button
                onClick={onBuyCredits}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'var(--color-accent-primary)',
                  color: 'var(--color-accent-primary-text)',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Buy Credits
              </button>
            </div>
          </div>
        ) : (
          <div 
            className="credits-history-tooltip"
            style={{
              position: 'fixed',
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              zIndex: 10000
            }}
          >
            <div className="tooltip-header">
              <span className="tooltip-title">Recent Activity</span>
              <span className="tooltip-count">{recentTransactions.filter(tx => tx.amount !== 0).length}</span>
            </div>
            <div className="tooltip-content">
              {recentTransactions.filter(tx => tx.amount !== 0).length === 0 ? (
                <div className="tooltip-empty">No recent activity</div>
              ) : (
                <ul className="history-list">
                  {recentTransactions.filter(tx => tx.amount !== 0).map((tx) => (
                    <li key={tx.id} className="history-item">
                      <span 
                        className="history-amount"
                        style={{ color: getTransactionColor(tx.type, tx.amount) }}
                      >
                        {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(1)}
                      </span>
                      <span className="history-desc" title={tx.description || `${tx.modelId || ''} ${tx.operationType || ''} ${tx.type}`.trim()}>
                        {tx.modelId && tx.operationType ? `${tx.modelId} • ${tx.operationType}` : tx.description || tx.modelId || tx.type}
                      </span>
                      <span className="history-time">{formatDate(tx.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ),
        document.body
      )}
    </>
  );
};

export default CreditsDisplay;
